import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoAddOutline, IoRemoveOutline } from 'react-icons/io5';
import { formatWorkoutNumber } from '../../utils/workoutNumberFormat';
import { nudgeIntensityInput, parseIntensityInput, powerInputLimit, resolvePowerInput, type PowerInputUnit } from '../../utils/powerIntensityInput';
import './workout-intensity-inputs.css';

interface Props {
  minValue: number; maxValue: number; ftp: number; unit: PowerInputUnit; single?: boolean;
  onChange: (values: { minValue: number; maxValue: number }) => void;
  onValidityChange: (valid: boolean) => void;
}
/** Drafts remain editable even when incomplete or temporarily reversed. Only valid pairs are committed. */
export function PowerIntensityInputs({ minValue, maxValue, ftp, unit, single = false, onChange, onValidityChange }: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const initial = (percent: number) => String(unit === 'watts' ? Math.round(percent * ftp / 100) : Number(percent.toFixed(6)));
  const [minText, setMinText] = useState(() => initial(minValue));
  const [maxText, setMaxText] = useState(() => initial(maxValue));
  const result = resolvePowerInput(minText, maxText, unit, ftp, single);
  const limit = powerInputLimit(unit, ftp);
  const locale = i18n.resolvedLanguage || i18n.language;
  const fmt = (value: number, decimals = 0) => formatWorkoutNumber(value, locale, { maximumFractionDigits: decimals, useGrouping: false });
  useEffect(() => { onValidityChange(result.valid); }, [result.valid, onValidityChange]);
  useEffect(() => () => onValidityChange(true), [onValidityChange]);
  const change = (bound: 'min' | 'max', text: string) => {
    const nextMin = bound === 'min' ? text : minText;
    const nextMax = bound === 'max' ? text : maxText;
    if (bound === 'min') setMinText(text); else setMaxText(text);
    const next = resolvePowerInput(nextMin, nextMax, unit, ftp, single);
    onValidityChange(next.valid);
    if (next.valid) onChange({ minValue: next.minValue, maxValue: next.maxValue });
  };
  const error = result.valid ? null : t(`workoutBuilder.intensityInputs.${result.error}`, { max: fmt(limit) });
  const fields = single ? (['min'] as const) : (['min', 'max'] as const);
  return <div className="wb-intensity-inputs">
    <div className="wb-intensity-inputs__fields">
      {fields.map(bound => {
        const text = bound === 'min' ? minText : maxText;
        const value = parseIntensityInput(text);
        const label = t(`workoutBuilder.intensityInputs.${single ? 'target' : bound}`);
        const fieldId = `${id}-${bound}`;
        const equivalent = value == null || !(ftp > 0) ? '—' : unit === 'percent'
          ? `${fmt(value * ftp / 100)} W` : `${fmt(value * 100 / ftp, 2)}% FTP`;
        return <div className="wb-intensity-inputs__field" key={bound}>
          <label htmlFor={fieldId}>{label} <bdi dir="ltr">({unit === 'percent' ? '% FTP' : 'W'})</bdi></label>
          <div className="wb-intensity-inputs__control" dir="ltr">
            <button type="button" className="wb-intensity-inputs__step" aria-label={t('workoutBuilder.intensityInputs.decrease', { field: label })} disabled={value != null && value <= 0} onClick={() => change(bound, nudgeIntensityInput(text, -1, limit))}><IoRemoveOutline aria-hidden="true" /></button>
            <input id={fieldId} type="text" inputMode="decimal" autoComplete="off" role="spinbutton" value={text} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={value ?? undefined} aria-invalid={!result.valid} aria-describedby={`${fieldId}-equivalent${error ? ` ${id}-error` : ''}`}
              onChange={event => change(bound, event.target.value)} onKeyDown={event => {
                if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                  event.preventDefault();
                  change(bound, nudgeIntensityInput(text, event.key === 'ArrowUp' ? 1 : -1, limit));
                }
              }} />
            <button type="button" className="wb-intensity-inputs__step" aria-label={t('workoutBuilder.intensityInputs.increase', { field: label })} disabled={value != null && value >= limit} onClick={() => change(bound, nudgeIntensityInput(text, 1, limit))}><IoAddOutline aria-hidden="true" /></button>
          </div>
          <bdi id={`${fieldId}-equivalent`} className="wb-intensity-inputs__equivalent" dir="ltr">{equivalent}</bdi>
        </div>;
      })}
    </div>
    {error && <p className="wb-intensity-inputs__error" id={`${id}-error`} role="alert">{error}</p>}
  </div>;
}
